import { useEffect, useState } from "react";
import { icons } from "../../assets";
import { createCartPaymentIntent, createCoffeePaymentIntent } from "../../api";
import { getStripePromise } from "../../stripe";
import CheckoutContactStep from "./CheckoutContactStep";
import CheckoutLocationStep from "./CheckoutLocationStep";
import CheckoutOrderSummary from "./CheckoutOrderSummary";
import CheckoutPaymentStep from "./CheckoutPaymentStep";
import CheckoutShippingStep from "./CheckoutShippingStep";
import {
  buildInitialBilling,
  getNextStep,
  getPrevStep,
  HOME_METHODS,
  isAddressComplete,
  isBillingComplete,
  LOCKER_METHODS,
} from "./checkoutUtils";

const EMPTY_ADDRESS = {
  postalCode: "",
  city: "",
  street: "",
  houseNumber: "",
};

const EMPTY_CUSTOMER = {
  name: "",
  email: "",
  phone: "",
};

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function getVisualStep(step) {
  if (step === "shipping") return 1;
  if (step === "location" || step === "contact") return 2;
  return 3;
}

function CheckoutStepper({ active, t }) {
  const steps = [
    { id: 1, label: t.stepShipping },
    { id: 2, label: t.stepDetails },
    { id: 3, label: t.stepPayment },
  ];

  return (
    <ol className="checkout-stepper" aria-label={t.orderTitle}>
      {steps.map((item, index) => {
        const isActive = active === item.id;
        const isDone = active > item.id;
        return (
          <li
            key={item.id}
            className={[
              "checkout-stepper-item",
              isActive ? "is-active" : "",
              isDone ? "is-done" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {index > 0 ? <span className="checkout-stepper-line" aria-hidden="true" /> : null}
            <span className="checkout-stepper-dot" aria-hidden="true">
              {isDone ? "✓" : item.id}
            </span>
            <span className="checkout-stepper-label">{item.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Shipping → details → payment. Buys the 1 kg coffee by default; with `cartLines`
 * it checks out a webshop cart instead.
 */
function CoffeeCheckoutFlow({
  shop,
  t,
  lang,
  onSuccess,
  cartLines = null,
  backHref = "",
  backLabel: shippingBackLabel = "",
  unavailableText = "",
}) {
  const isCart = Array.isArray(cartLines);
  const subtotalHuf = isCart
    ? cartLines.reduce((sum, line) => sum + line.lineTotalHuf, 0)
    : shop.coffeePriceHuf;

  function createPaymentIntent(payload) {
    if (!isCart) return createCoffeePaymentIntent(payload);
    return createCartPaymentIntent({
      ...payload,
      items: cartLines.map((line) => ({ productId: line.productId, quantity: line.quantity })),
    });
  }

  function paymentErrorText(err) {
    return err?.message === "product_unavailable" && unavailableText ? unavailableText : t.error;
  }

  const [step, setStep] = useState("shipping");
  const [shippingMethod, setShippingMethod] = useState("pickup");
  const [pickupPoint, setPickupPoint] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState(true);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [billing, setBilling] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [amountTotalHuf, setAmountTotalHuf] = useState(0);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!shop?.shipping) return;

    const selected = shop.shipping[shippingMethod];
    if (selected?.enabled) return;

    const fallback = ["pickup", "foxpostLocker", "glsLocker", "foxpostHome", "glsHome"].find(
      (id) => shop.shipping[id]?.enabled,
    );
    if (fallback) {
      setShippingMethod(fallback);
    }
  }, [shop, shippingMethod]);

  useEffect(() => {
    let cancelled = false;

    async function loadStripe() {
      const stripe = await getStripePromise();
      if (!cancelled) {
        setStripePromise(stripe);
      }
    }

    loadStripe();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (
      step !== "payment" ||
      clientSecret ||
      loading ||
      !billing ||
      !isBillingComplete(billing)
    ) {
      return undefined;
    }

    let cancelled = false;

    async function startPayment() {
      setLoading(true);
      setError("");

      try {
        const data = await createPaymentIntent({
          shippingMethod,
          lang,
          customer,
          billing,
          pickupPoint: LOCKER_METHODS.has(shippingMethod) ? pickupPoint : undefined,
          shippingAddress: HOME_METHODS.has(shippingMethod)
            ? shippingSameAsBilling
              ? billingAddress
              : shippingAddress
            : undefined,
        });

        if (!cancelled) {
          setClientSecret(data.clientSecret);
          setAmountTotalHuf(data.amountTotalHuf);
        }
      } catch (err) {
        if (!cancelled) {
          setError(paymentErrorText(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    startPayment();

    return () => {
      cancelled = true;
    };
  }, [
    step,
    clientSecret,
    loading,
    shippingMethod,
    lang,
    customer,
    billing,
    billingAddress,
    pickupPoint,
    shippingAddress,
    shippingSameAsBilling,
    t.error,
  ]);

  function validateCurrentStep() {
    if (step === "shipping" && !shippingMethod) {
      return t.validationShipping;
    }

    if (step === "location") {
      if (LOCKER_METHODS.has(shippingMethod) && !pickupPoint) {
        return t.validationPickup;
      }
      if (HOME_METHODS.has(shippingMethod)) {
        if (!isAddressComplete(billingAddress)) {
          return t.validationBillingAddress;
        }
        if (!shippingSameAsBilling && !isAddressComplete(shippingAddress)) {
          return t.validationAddress;
        }
      }
    }

    if (step === "contact") {
      if (!customer.name.trim() || !customer.phone.trim()) {
        return t.validationContact;
      }
      if (!isValidEmail(customer.email)) {
        return t.validationEmail;
      }
      if (shippingMethod === "pickup" && !isAddressComplete(billingAddress)) {
        return t.validationBillingAddress;
      }
    }

    return "";
  }

  function handleContinue() {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    const nextStep = getNextStep(step, shippingMethod);

    let nextShippingAddress = shippingAddress;
    if (step === "location" && HOME_METHODS.has(shippingMethod) && shippingSameAsBilling) {
      nextShippingAddress = { ...billingAddress };
      setShippingAddress(nextShippingAddress);
    }

    if (nextStep === "payment") {
      const nextBilling = buildInitialBilling(customer, billingAddress, shippingMethod);
      setBilling(nextBilling);
      setClientSecret("");
      setAmountTotalHuf(0);
      setLoading(true);

      void createPaymentIntent({
        shippingMethod,
        lang,
        customer,
        billing: nextBilling,
        pickupPoint: LOCKER_METHODS.has(shippingMethod) ? pickupPoint : undefined,
        shippingAddress: HOME_METHODS.has(shippingMethod)
          ? shippingSameAsBilling
            ? billingAddress
            : nextShippingAddress
          : undefined,
      })
        .then((data) => {
          setClientSecret(data.clientSecret);
          setAmountTotalHuf(data.amountTotalHuf);
        })
        .catch((err) => {
          setError(paymentErrorText(err));
        })
        .finally(() => {
          setLoading(false);
        });
    }

    setStep(nextStep);
  }

  function handlePaymentSuccess() {
    onSuccess?.();
  }

  function handleBack() {
    setError("");
    if (step === "payment") {
      setClientSecret("");
      setAmountTotalHuf(0);
    }
    setStep(getPrevStep(step, shippingMethod));
  }

  function handleTopBack() {
    if (step === "shipping") {
      window.location.href = backHref || `/?lang=${lang}#mizu-coffee`;
      return;
    }
    handleBack();
  }

  function handleShippingSelect(method) {
    setShippingMethod(method);
    setPickupPoint(null);
    setShippingAddress(EMPTY_ADDRESS);
    setBillingAddress(EMPTY_ADDRESS);
    setShippingSameAsBilling(true);
    setError("");
  }

  const backLabel =
    step === "shipping"
      ? shippingBackLabel || t.backToHome
      : step === "contact" || step === "location"
        ? t.backToShipping
        : t.back;
  const isPaymentStep = step === "payment";
  const isLockerMapStep =
    step === "location" &&
    (shippingMethod === "foxpostLocker" || shippingMethod === "glsLocker");
  const continueLabel =
    step === "shipping"
      ? t.continueToDetails
      : step === "location"
        ? t.continue
        : t.continueToPayment;

  return (
    <div
      className={[
        "checkout-page-panel",
        isLockerMapStep ? "checkout-page-panel--map-focus" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="checkout-modal-top">
        <div className="checkout-modal-top-side checkout-modal-top-side--left">
          <button type="button" className="checkout-modal-back" onClick={handleTopBack}>
            ← {backLabel}
          </button>
        </div>

        <CheckoutStepper active={getVisualStep(step)} t={t} />

        <div className="checkout-modal-top-side checkout-modal-top-side--right" />
      </div>

      <div
        className={[
          "checkout-modal-layout",
          isLockerMapStep ? "checkout-modal-layout--map-focus" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="checkout-modal-main">
          <div className="checkout-modal-body">
            {step === "shipping" ? (
              <>
                <h3 className="checkout-section-title">{t.howToReceive}</h3>
                <CheckoutShippingStep
                  shop={shop}
                  t={t}
                  shippingMethod={shippingMethod}
                  onSelect={handleShippingSelect}
                />
              </>
            ) : null}

            {step === "location" ? (
              <CheckoutLocationStep
                t={t}
                lang={lang}
                shippingMethod={shippingMethod}
                shippingAddress={shippingAddress}
                billingAddress={billingAddress}
                shippingSameAsBilling={shippingSameAsBilling}
                pickupPoint={pickupPoint}
                onShippingAddressChange={setShippingAddress}
                onBillingAddressChange={setBillingAddress}
                onShippingSameAsBillingChange={setShippingSameAsBilling}
                onPickupSelect={setPickupPoint}
              />
            ) : null}

            {isLockerMapStep ? (
              <button
                type="button"
                className="checkout-summary-cta checkout-map-continue"
                disabled={loading}
                onClick={handleContinue}
              >
                {loading ? t.loading : continueLabel}
              </button>
            ) : null}

            {step === "contact" ? (
              <CheckoutContactStep
                t={t}
                customer={customer}
                onChange={setCustomer}
                shippingMethod={shippingMethod}
                billingAddress={billingAddress}
                onBillingAddressChange={setBillingAddress}
              />
            ) : null}

            {step === "payment" && billing ? (
              <CheckoutPaymentStep
                t={t}
                lang={lang}
                clientSecret={clientSecret}
                stripePromise={stripePromise}
                paymentLoading={loading}
                billing={billing}
                customer={customer}
                amountTotalHuf={
                  amountTotalHuf ||
                  subtotalHuf + (shop.shipping?.[shippingMethod]?.priceHuf || 0)
                }
                returnQuery={isCart ? "&from=shop" : ""}
                onSuccess={handlePaymentSuccess}
                onError={setError}
              />
            ) : null}

            {error ? <p className="checkout-modal-error">{error}</p> : null}
          </div>
        </div>

        {!isLockerMapStep ? (
          <CheckoutOrderSummary
            t={t}
            shop={shop}
            shippingMethod={shippingMethod}
            pickupPoint={pickupPoint}
            shippingAddress={shippingAddress}
            billingAddress={billingAddress}
            shippingSameAsBilling={shippingSameAsBilling}
            coffeeImage={icons.coffee}
            cartLines={cartLines}
            showContinue={!isPaymentStep}
            continueLabel={loading ? t.loading : continueLabel}
            continueDisabled={loading}
            onContinue={handleContinue}
          />
        ) : null}
      </div>
    </div>
  );
}

export default CoffeeCheckoutFlow;
